// (c) chaehongee. Confidential and proprietary.

import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

export interface CanvasItem {
    id: string
    src: string
    mediaType?: string
}

export interface CanvasState {
    items: CanvasItem[]
}

interface CanvasSliceState {
    bySessionId: Record<string, CanvasState>
}

const initialCanvasState = (): CanvasState => ({ items: [] })

const initialState: CanvasSliceState = { bySessionId: {} }

function getCanvasState(state: CanvasSliceState, sessionId: string): CanvasState {
    if (!state.bySessionId[sessionId]) {
        state.bySessionId[sessionId] = initialCanvasState()
    }
    return state.bySessionId[sessionId]
}

const canvasSlice = createSlice({
    name: 'canvas',
    initialState,
    reducers: {
        addItem: (
            state,
            action: PayloadAction<{ sessionId: string; item: CanvasItem }>
        ) => {
            const s = getCanvasState(state, action.payload.sessionId)
            const exists = s.items.some((i) => i.id === action.payload.item.id)
            if (!exists) s.items.push(action.payload.item)
        },
        addItems: (
            state,
            action: PayloadAction<{ sessionId: string; items: CanvasItem[] }>
        ) => {
            const s = getCanvasState(state, action.payload.sessionId)
            const existing = new Set(s.items.map((i) => i.id))
            for (const it of action.payload.items) {
                if (!existing.has(it.id)) s.items.push(it)
            }
        },
        removeItem: (
            state,
            action: PayloadAction<{ sessionId: string; itemId: string }>
        ) => {
            const s = getCanvasState(state, action.payload.sessionId)
            s.items = s.items.filter((i) => i.id !== action.payload.itemId)
        },
        clearItems: (state, action: PayloadAction<{ sessionId: string }>) => {
            state.bySessionId[action.payload.sessionId] = initialCanvasState()
        },
        setItems: (
            state,
            action: PayloadAction<{ sessionId: string; items: CanvasItem[] }>
        ) => {
            const s = getCanvasState(state, action.payload.sessionId)
            s.items = Array.isArray(action.payload.items) ? action.payload.items : []
        },
        updateItemSrc: (
            state,
            action: PayloadAction<{
                sessionId: string
                id: string
                src: string
            }>
        ) => {
            const s = getCanvasState(state, action.payload.sessionId)
            const target = s.items.find((i) => i.id === action.payload.id)
            if (target) {
                target.src = action.payload.src
            }
        },
        removeCanvasState: (state, action: PayloadAction<{ sessionId: string }>) => {
            delete state.bySessionId[action.payload.sessionId]
        },
    },
})

export const {
    addItem,
    addItems,
    removeItem,
    clearItems,
    setItems,
    updateItemSrc,
    removeCanvasState,
} = canvasSlice.actions
export default canvasSlice.reducer


